import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, BadgeCheck, HeartHandshake, ShieldCheck, ArrowRight, RotateCcw, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { trackEvent, fetchPublicSettings, createJourney } from "@/lib/api";
import { setConsent, hasConsent, loadQuizState, captureSource } from "@/lib/quizStorage";
import { LOGO_WHITE, TAGLINE } from "@/lib/branding";

const reassuranceItems = [
  { icon: Clock, title: "Environ 5 minutes", text: "Un parcours court, pensé pour ton quotidien." },
  { icon: BadgeCheck, title: "Résultat personnalisé immédiat", text: "Ton profil CYCLIQ dès la fin du diagnostic." },
  { icon: HeartHandshake, title: "Aucun jugement", text: "Aucune réponse parfaite n'est attendue." },
  { icon: ShieldCheck, title: "Pas un diagnostic médical", text: "Un outil d'orientation, jamais un avis médical." },
];

const steps = [
  { number: "01", title: "Réponds à quelques questions", text: "24 questions simples sur ton énergie, ton entraînement, tes habitudes et ton rythme de vie." },
  { number: "02", title: "Découvre ton profil CYCLIQ", text: "Un bilan personnalisé : tes forces, tes priorités et ta première action concrète." },
  { number: "03", title: "Échange avec Gino", text: "Si tu le souhaites, réserve un échange pour interpréter ton bilan et construire ta stratégie personnalisée." },
];

export default function IntroPage() {
  const navigate = useNavigate();
  const [consentChecked, setConsentChecked] = useState(hasConsent());
  const [hasSavedQuiz, setHasSavedQuiz] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    captureSource();
    trackEvent("page_viewed");
    fetchPublicSettings().then(setSettings).catch(() => setSettings(null));
    const saved = loadQuizState();
    setHasSavedQuiz(Boolean(saved && saved.answers && Object.keys(saved.answers).length > 0 && !saved.completed));
    window.scrollTo(0, 0);
  }, []);

  const registerConsentProof = () => {
    // Preuve minimale du consentement sensible : identifiant aléatoire,
    // version du texte, date. AUCUNE réponse, AUCUNE donnée de santé.
    const version = settings?.consent_texts?.version || "";
    createJourney(version).catch(() => { /* jamais bloquant */ });
  };

  const startDiagnostic = () => {
    if (!consentChecked) return;
    setConsent();
    registerConsentProof();
    navigate("/diagnostic");
  };

  const resumeDiagnostic = () => {
    setConsent();
    navigate("/diagnostic");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="gbs-hero-wash">
          <div className="max-w-[1040px] mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-12 sm:pb-16">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-[720px]"
            >
              <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#235277] mb-4">
                Diagnostic CYCLIQ™ par Gino Body &amp; Soul
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-[#060606]">
                Et si ton corps ne manquait pas de discipline,{" "}
                <span className="relative inline-block isolate">
                  <span className="relative z-10">mais de synchronisation ?</span>
                  <span className="absolute left-0 bottom-1 h-3.5 w-full bg-[#EAFE63] z-0 rounded-sm" aria-hidden="true" />
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#333333] max-w-[560px]">
                Réponds à quelques questions pour identifier les domaines qui influencent
                aujourd’hui ton énergie, ta régularité et ta progression.
              </p>
              <blockquote className="mt-6 border-l-2 border-[#235277] pl-4">
                <p className="font-heading text-lg sm:text-xl font-semibold text-[#235277] italic">
                  « Tu n’es pas instable. Tu es cyclique. »
                </p>
              </blockquote>
              <p className="mt-4 text-sm text-[#9E9E9E]">
                Conçu par Gino Fidelin, éducateur sportif, plus de 20 ans d’expérience terrain.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Reassurance */}
        <section className="max-w-[1040px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {reassuranceItems.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-4 sm:p-5 shadow-[var(--shadow-sm)]"
              >
                <item.icon className="w-5 h-5 text-[#235277]" aria-hidden="true" />
                <h3 className="font-heading mt-3 text-sm font-semibold text-[#060606]">{item.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-[#333333]">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-[1040px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#060606] mb-8">
            Comment ça se passe
          </h2>
          <div className="space-y-6 max-w-[640px]">
            {steps.map((step) => (
              <div key={step.number} className="flex gap-4 items-start">
                <span className="font-heading shrink-0 w-11 h-11 rounded-full bg-[#235277] text-[#EAFE63] flex items-center justify-center text-sm font-bold">
                  {step.number}
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-[#060606]">{step.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#333333]">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Consent + CTA */}
        <section className="max-w-[1040px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="relative rounded-2xl bg-[#235277] p-6 sm:p-10 overflow-hidden gbs-grain">
            <div className="absolute inset-0 gbs-lemon-halo pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-[560px]">
              <img
                src={LOGO_WHITE}
                alt="Gino Body & Soul"
                className="h-10 w-auto mb-1.5"
                data-testid="intro-cta-logo-image"
              />
              <p className="text-[11px] font-medium text-[rgba(249,254,255,0.75)] mb-5" data-testid="intro-cta-tagline">
                {TAGLINE}
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                Prête à comprendre ton fonctionnement ?
              </h2>

              {/* Avertissement imposé : conservation nulle */}
              <div
                className="mt-4 flex items-start gap-3 rounded-xl bg-[rgba(249,254,255,0.08)] border border-[rgba(249,254,255,0.2)] px-4 py-3"
                data-testid="intro-zero-retention-warning"
              >
                <Lock className="w-4 h-4 shrink-0 mt-0.5 text-[#EAFE63]" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-[rgba(249,254,255,0.92)]">
                  Tes réponses restent dans ton navigateur, et nulle part ailleurs. Rien n’est
                  envoyé sur nos serveurs : ton bilan est calculé directement sur ton appareil,
                  ni CYCLIQ ni nos prestataires techniques n’y ont accès. Une seule chose à
                  retenir : télécharge ton bilan ou demande son envoi par email avant de fermer
                  la page. Sinon, tout est effacé et il faudra recommencer le diagnostic.
                </p>
              </div>

              <div className="mt-6 flex items-start gap-3">
                <Checkbox
                  id="consent"
                  data-testid="intro-consent-checkbox"
                  checked={consentChecked}
                  onCheckedChange={(value) => setConsentChecked(Boolean(value))}
                  className="mt-0.5 border-[rgba(249,254,255,0.6)] data-[state=checked]:bg-[#EAFE63] data-[state=checked]:text-[#060606] data-[state=checked]:border-[#EAFE63]"
                />
                <label htmlFor="consent" className="text-sm leading-relaxed text-[rgba(249,254,255,0.92)] cursor-pointer">
                  J’accepte que mes réponses, dont certaines touchent à ma santé, soient utilisées
                  dans mon navigateur pour calculer mon bilan. Elles ne sont ni conservées ni
                  transmises. Si je demande mon bilan par email, il est chiffré sur mon appareil
                  avant l’envoi.{" "}
                  <Link to="/confidentialite" data-testid="intro-privacy-link" className="underline underline-offset-2 text-[#EAFE63]">
                    Politique de confidentialité
                  </Link>
                </label>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="intro-start-diagnostic-button"
                  onClick={startDiagnostic}
                  disabled={!consentChecked}
                  className="h-13 min-h-[52px] px-7 rounded-xl bg-[#EAFE63] text-[#060606] font-heading font-semibold text-base hover:bg-[#e0f65a] active:scale-[0.98] shadow-[var(--shadow-cta)] disabled:opacity-40 transition-colors duration-200"
                >
                  Commencer mon diagnostic
                  <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                </Button>
                {hasSavedQuiz && (
                  <Button
                    data-testid="intro-resume-diagnostic-button"
                    onClick={resumeDiagnostic}
                    variant="outline"
                    className="h-13 min-h-[52px] px-6 rounded-xl border-[rgba(249,254,255,0.4)] bg-transparent text-white hover:bg-[rgba(249,254,255,0.1)] hover:text-white font-heading font-semibold transition-colors duration-200"
                  >
                    <RotateCcw className="mr-2 w-4 h-4" aria-hidden="true" />
                    Reprendre mon diagnostic
                  </Button>
                )}
              </div>

              <p className="mt-5 text-xs text-[rgba(249,254,255,0.6)]">
                Ce bilan ne constitue pas un diagnostic médical et ne remplace pas l’avis
                d’un professionnel de santé qualifié.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
